<script setup lang="ts">
import { useComputedAttrs, useComputedInput, useEventsToValidate } from './helpers'
import { type CheckboxInputProps } from './types'

const props = defineProps<CheckboxInputProps>()
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'validate', 'invalid'])
const input = useComputedInput(props, emit)
const attrs = useComputedAttrs(props, 'checkbox')
const events = useEventsToValidate(emit)

function setIndeterminate(el: HTMLInputElement, value: unknown) {
  el.indeterminate = Boolean(value)
}

const vIndeterminate = {
  mounted: (el: HTMLInputElement, { value }: { value: unknown }) => setIndeterminate(el, value),
  updated: (el: HTMLInputElement, { value }: { value: unknown }) => setIndeterminate(el, value),
}
</script>

<template>
  <input
    v-model="input"
    v-autofocus="props.autofocus"
    v-bind="attrs"
    v-indeterminate="props.indeterminate"
    type="checkbox"
    :aria-invalid="props.invalid"
    :aria-checked="props.indeterminate ? 'mixed' : undefined"
    :tabindex="props.tabindex"
    v-on="events"
    :required="props.required"
  />
</template>

<style scoped>
input {
  /* Size in em so it scales with font-size; FormInput sets font-size on .input-wrapper input[type='checkbox'] from the size class. */
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  width: 1.5em;
  height: 1.5em;
  flex-shrink: 0;
  border: 1px solid var(--c-checkbox-off-border);
  border-radius: 33%;
  background: var(--c-input-background);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background var(--transition-duration) ease,
    border-color var(--transition-duration) ease;

  &:hover:not(:disabled) {
    background: var(--c-checkbox-off-bg-hover);
    border-color: var(--c-checkbox-off-border-hover);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--c-input-focus-visible);
  }

  &:disabled {
    background: var(--c-checkbox-off-disabled-bg);
    border-color: var(--c-checkbox-off-disabled-border);
    cursor: default;
  }

  &:checked:not(:indeterminate) {
    background: var(--c-checkbox-on-bg);
    border-color: var(--c-checkbox-on-border);

    &:hover:not(:disabled) {
      background: var(--c-checkbox-on-bg-hover);
      border-color: var(--c-checkbox-on-border-hover);
    }

    &:focus {
      background: var(--c-checkbox-on-bg-focus);
      border-color: var(--c-checkbox-on-border-focus);
    }

    &:focus-visible {
      box-shadow: var(--c-input-focus-visible);
    }

    &::after {
      content: '';
      width: 24%;
      height: 45%;
      top: -5%;
      position: relative;
      border-bottom: 2px solid var(--c-checkbox-checked-icon);
      border-right: 2px solid var(--c-checkbox-checked-icon);
      transform: rotate(45deg);
      transform-origin: center center;
    }

    &:disabled {
      background: var(--c-checkbox-on-disabled-bg);
      border-color: var(--c-checkbox-on-disabled-border);

      &::after {
        border-bottom-color: var(--c-checkbox-checked-disabled-icon);
        border-right-color: var(--c-checkbox-checked-disabled-icon);
      }
    }
  }

  &:indeterminate {
    background: var(--c-checkbox-on-bg);
    border-color: var(--c-checkbox-on-border);

    &:hover:not(:disabled) {
      background: var(--c-checkbox-on-bg-hover);
      border-color: var(--c-checkbox-on-border-hover);
    }

    &:focus {
      background: var(--c-checkbox-on-bg-focus);
      border-color: var(--c-checkbox-on-border-focus);
    }

    &:focus-visible {
      box-shadow: var(--c-input-focus-visible);
    }

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 55%;
      height: 2px;
      background: var(--c-checkbox-checked-icon);
      transform: translate(-50%, -50%);
    }

    &:disabled {
      background: var(--c-checkbox-on-disabled-bg);
      border-color: var(--c-checkbox-on-disabled-border);

      &::after {
        background: var(--c-checkbox-checked-disabled-icon);
      }
    }
  }
}
</style>
